import './ProjectEvolutionTab.scss';

import plusIcon from 'assets/images/icons/plus.svg';
import tlNextIcon from 'assets/images/icons/arrow-timeline-next.svg';
import tlPrevIcon from 'assets/images/icons/arrow-timeline-prev.svg';
import eyeLight from 'assets/images/icons/eye-light.svg';
import trashCan from 'assets/images/icons/trashcan.svg';
import download from 'assets/images/icons/download.svg';
import penIcon from 'assets/images/icons/pen.svg';

import { Button } from 'components/Button/Button';
import { Chip } from 'components/Chip/Chip';
import { ContentBlock } from '../../../../components/ContentBlock/ContentBlock';
import useProjectEvolutionTabVM from './useProjectEvolutionTabVM';

import moment from 'moment';
import { useNavigate } from 'react-router-dom';
import { Loader } from 'components/Loader/Loader';
import { FileUploader } from 'components/FileUploader/FileUploader';
import Table from 'components/Table/Table';
import { useState } from 'react';
import { ApiFile } from 'utils/useFlowTs';
import { FileWidget } from 'components/FileUploader/FileWidget/FileWidget';
import { Person } from 'services/person/person.types';
import sanitizeHtml from 'sanitize-html';

export default function ProjectEvolutionTab({ person }: { person: Person }) {
    const {
        events,
        personId,
        projectId,
        fillerEvent,
        datalists,
        setTimelineStep,
        timelineMaxSteps,
        isGetPersonProjectEventsLoading,
        timelineStep,
        selectedEvent,
        emptyEventsCount,
        handleFileDeleted,
        handleFileUpdated,
        handleFileAdded,
        setSelectedEvent
    } = useProjectEvolutionTabVM(person);
    const navigate = useNavigate();

    function renderTimeline() {
        if (events.length === 0) return <>Aucun évènement enregistré</>;
        const emptyEvents = [...Array(emptyEventsCount)].fill(fillerEvent);

        return (
            <>
                <div className={`arrow prev ${timelineStep === 0 ? ' disabled' : ''}`} onClick={(e) => setTimelineStep(timelineStep - 1)}>
                    <img src={tlPrevIcon} alt="" />
                </div>
                <div className="timeline-container">
                    <div
                        className="timeline"
                        style={{
                            translate: `-${timelineStep * 100}% 0`
                        }}
                    >
                        {[...events, ...emptyEvents].map((event, index) => {
                            return (
                                <div
                                    className={`timeline-event${selectedEvent?.id === event.id ? ' active' : ''}${event.id === -1 ? ' filler' : ''}`}
                                    key={event.reference !== '' ? event.reference : 'event-filler' + index}
                                    onClick={() => setSelectedEvent(event)}
                                >
                                    {event.id !== -1 ? (
                                        <>
                                            {moment(event.started_at).fromNow()}
                                            <br />
                                        </>
                                    ) : (
                                        ''
                                    )}
                                    <span className="title">{event.label}</span>
                                </div>
                            );
                        })}
                    </div>
                </div>
                <div
                    className={`arrow next${timelineStep + 1 >= timelineMaxSteps ? ' disabled' : ''}`}
                    onClick={(e) => setTimelineStep(timelineStep + 1)}
                >
                    <img src={tlNextIcon} alt="" />
                </div>
            </>
        );
    }


    if (isGetPersonProjectEventsLoading) return <Loader />;

    return (
        <section id="project-evolution">
            <div className="heading">
                <Button
                    label="Ajouter un évènement"
                    variant="light"
                    icon={<img src={plusIcon} alt="" />}
                    iconBefore
                    callback={() => {
                        navigate(`/app/admin/persons/${personId}/project/${projectId}/events/new`);
                    }}
                />
                {person.projects?.[0] && <Chip color="primary" label={`${Math.floor(person.projects?.[0]?.total_events_duration / 60 / 60)}h ${Math.round((person.projects?.[0]?.total_events_duration / 60) % 60)}min passées`} />}
            </div>
            {!selectedEvent && <div className='empty-state'>Aucun évènement n'a encore été ajouté</div>}
            {selectedEvent && (
                <>
                    <ContentBlock className="event-timeline">{renderTimeline()}</ContentBlock>
                    <div className="event">
                        <div className="event-title">
                            <span className="title">{selectedEvent?.label}&nbsp;
                                <img src={penIcon} className="edit-icon" alt="" title="Éditer l'évènement"
                                    onClick={() => {
                                        navigate(`/app/admin/persons/${personId}/project/${projectId}/events/${selectedEvent.id}/edit`)
                                    }} />
                            </span>
                            <Chip
                                compact
                                label={moment(selectedEvent?.started_at).format('Do MMM YYYY')}
                                color="primary"
                                className="date"
                            />
                        </div>
                        <div className="event-blocks">
                            <ContentBlock title="Documents liés" className="documents">
                                <div className='form-line'>
                                    <div className="form-group">
                                        <FileWidget documents={selectedEvent.documents as unknown as ApiFile[]} onUpdateFile={(file) => {handleFileUpdated(file as unknown as ApiFile)}} onDeleteFile={(file) => {handleFileDeleted(file as unknown as ApiFile)}} onAddFile={(file) => {handleFileAdded(file as unknown as ApiFile)}} />
                                    </div>
                                </div>
                            </ContentBlock>
                            <ContentBlock title="Entretien" className="interview">
                                <p className="subtitle">Communication</p>
                                <p>
                                    {/^\d+$/.test(selectedEvent.communication_type)
                                        ? datalists?.['communication_types']?.[selectedEvent.communication_type]
                                        : selectedEvent.communication_type}
                                </p>
                                <p className="subtitle">Personnes présentes</p>
                                <div className="attendees">
                                    {selectedEvent.stakeholders_list.map((sh) => {
                                        return (
                                            <div className="name" key={"stakeholder-" + sh.value}>
                                                {sh.label} <span>(Proche)</span>
                                            </div>
                                        );
                                    })}
                                    {selectedEvent.assistants_list.map((assistant) => {
                                        return <div className="name" key={"assistant-" + assistant.value}>{assistant.label}</div>;
                                    })}
                                    {selectedEvent.partners_list.map((partner) => {
                                        return <div className="name" key={"partner-" + partner.value}>{partner.label}</div>;
                                    })}
                                    {selectedEvent.emails_list.map((email) => {
                                        return <div className="name" key={"email-" + email.value}>{email.label}</div>;
                                    })}
                                </div>
                                {/* <p className="subtitle">Temps passé</p> */}
                            </ContentBlock>
                            <ContentBlock title="Commentaire" className="comment">
                                <div dangerouslySetInnerHTML={{ __html: sanitizeHtml(selectedEvent.observation) }} />
                            </ContentBlock>
                            {selectedEvent.user_comment && (
                                <ContentBlock title="Commentaire de la personne soutenue" className="comment" postIt>
                                    <p style={{ whiteSpace: 'pre-wrap' }}>{selectedEvent.user_comment}</p>
                                </ContentBlock>
                            )}
                            {selectedEvent.user_next_meeting_preparation && (
                                <ContentBlock title="Préparation du prochain entretien (personne soutenue)" className="comment" postIt>
                                    <p style={{ whiteSpace: 'pre-wrap' }}>{selectedEvent.user_next_meeting_preparation}</p>
                                </ContentBlock>
                            )}
                        </div>
                    </div>
                </>
            )}
        </section>
    );
}
